import { WorkflowChatTransport } from "@ai-sdk/workflow";
import type {
  ChatMessage,
  ChatAgentId,
  PostChatRequestBody,
  PostChatRequestContext,
} from "@tracify/shared";

type UserChatMessage = ChatMessage & { role: "user" };

function isUserChatMessage(
  message: ChatMessage | undefined
): message is UserChatMessage {
  return message?.role === "user";
}

function createTransportFetch(fetchImpl: typeof globalThis.fetch) {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await fetchImpl(input, init);
    if (response.status !== 204) {
      return response;
    }

    const rawUrl =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.toString()
          : input;
    const url = new URL(
      rawUrl,
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin
    );
    const isReconnectRequest =
      url.pathname.endsWith("/stream") && url.searchParams.has("startIndex");
    if (!isReconnectRequest) {
      return response;
    }

    return new Response('data: {"type":"finish"}\n\n', {
      status: 200,
      headers: {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache",
      },
    });
  };
}

export function buildPostChatRequestBody({
  chatId,
  messages,
  trigger,
  messageId,
  agentId,
  requestContext,
}: {
  chatId: string;
  messages: ChatMessage[];
  trigger: "submit-message" | "regenerate-message";
  messageId?: string;
  agentId: ChatAgentId;
  requestContext: PostChatRequestContext;
}): PostChatRequestBody {
  const baseBody = {
    agentId,
    id: chatId,
    trigger,
    requestContext,
  };
  const lastMessage = messages.at(-1);

  if (trigger === "submit-message" && isUserChatMessage(lastMessage)) {
    return {
      ...baseBody,
      message: lastMessage,
    } as PostChatRequestBody;
  }

  return {
    ...baseBody,
    messages,
    ...(messageId ? { messageId } : {}),
  } as PostChatRequestBody;
}

export function createTracifyChatTransport({
  api,
  fetch,
  getAuthToken,
  getAgentId,
  getRequestContext,
}: {
  api: string;
  fetch: typeof globalThis.fetch;
  getAuthToken: () => string;
  getAgentId: () => ChatAgentId;
  getRequestContext: () => PostChatRequestContext;
}) {
  return new WorkflowChatTransport<ChatMessage>({
    api,
    fetch: createTransportFetch(fetch),
    prepareSendMessagesRequest({ id, messages, headers, trigger, messageId }) {
      return {
        body: buildPostChatRequestBody({
          agentId: getAgentId(),
          chatId: id,
          messages,
          trigger,
          messageId,
          requestContext: getRequestContext(),
        }),
        headers: {
          "Content-Type": "application/json",
          ...headers,
          "tracify-token": getAuthToken(),
        },
      };
    },
    prepareReconnectToStreamRequest({ id, headers, ...rest }) {
      return {
        ...rest,
        api: `${api}/${encodeURIComponent(id)}/stream`,
        headers: {
          ...headers,
          "tracify-token": getAuthToken(),
        },
      };
    },
  });
}
